import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { LifeBuoy, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function Support() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name || !email || !message) { setError("All fields are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/support/contact", { name, email, message, category });
      setSent(data.ticket_id);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div data-testid="support-card" className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#0F1117] p-8">
        <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 mb-6 font-mono uppercase tracking-widest">
          <ArrowLeft className="h-3.5 w-3.5" /> Home
        </Link>
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 mb-4">
          <LifeBuoy className="h-3.5 w-3.5 text-amber-300" />
          <span className="font-mono text-[11px] uppercase tracking-widest text-amber-200">Support · contact</span>
        </div>
        <h1 className="font-serif text-3xl font-bold mb-2">How can we help?</h1>
        <p className="text-slate-400 text-sm mb-6">
          Hit a bug, got a billing question, or want to report content? Drop us a line — we read every ticket.
        </p>

        {sent ? (
          <div data-testid="support-sent" className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 px-4 py-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-serif text-lg">Message received.</div>
              <div className="text-sm mt-1">Reference ID: <code className="font-mono text-xs">{sent}</code>. We'll reply to <b>{email}</b> as soon as we can.</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">Your name</Label>
              <Input data-testid="support-name" value={name} onChange={e => setName(e.target.value)}
                     className="mt-1 bg-[#07080A] border-white/10 h-11" required />
            </div>
            <div>
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">Email</Label>
              <Input data-testid="support-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                     className="mt-1 bg-[#07080A] border-white/10 h-11" required />
            </div>
            <div>
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="support-category" className="mt-1 bg-[#07080A] border-white/10 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="bug">Bug / technical issue</SelectItem>
                  <SelectItem value="billing">Billing / subscription</SelectItem>
                  <SelectItem value="content">Report content</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">Message</Label>
              <Textarea data-testid="support-message" value={message} onChange={e => setMessage(e.target.value)}
                        className="mt-1 bg-[#07080A] border-white/10 min-h-[120px]" required />
            </div>
            {error && <div data-testid="support-error" className="rounded-xl border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm px-4 py-3">{error}</div>}
            <Button data-testid="support-submit" type="submit" disabled={busy}
                    className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send message"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
